/**
 * Keeps every open tab on the active account.
 *
 * The browser has one active account at a time (the session cookie). When a
 * tab starts, it announces its account to the other open tabs, and tabs
 * signed in as someone else reload the page they are on as the new account.
 *
 * As a fallback (e.g. without BroadcastChannel), the server refuses actions
 * from a tab whose account is no longer active (see
 * Api::ActiveWebSessionConcern), which also triggers the reload.
 */

const CHANNEL_NAME = 'mastodon-active-account';

interface ActiveAccountMessage {
  type: 'active-account';
  accountId: string;
}

function isActiveAccountMessage(data: unknown): data is ActiveAccountMessage {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const { type, accountId } = data as Record<string, unknown>;

  return type === 'active-account' && typeof accountId === 'string';
}

let reloadPage = () => {
  window.location.reload();
};

let reloading = false;

function reloadAsActiveAccount() {
  if (reloading) {
    return;
  }

  reloading = true;
  reloadPage();
}

/** Called when the server refused a request because the account changed. */
export function handleAccountSwitched() {
  reloadAsActiveAccount();
}

export function startAccountSwitchSync(
  currentAccountId: string | null | undefined,
  { reload }: { reload?: () => void } = {},
) {
  if (reload) {
    reloadPage = reload;
  }

  reloading = false;

  if (!currentAccountId || typeof BroadcastChannel === 'undefined') {
    return () => undefined;
  }

  const channel = new BroadcastChannel(CHANNEL_NAME);

  channel.addEventListener('message', (event: MessageEvent<unknown>) => {
    if (
      isActiveAccountMessage(event.data) &&
      event.data.accountId !== currentAccountId
    ) {
      reloadAsActiveAccount();
    }
  });

  const message: ActiveAccountMessage = {
    type: 'active-account',
    accountId: currentAccountId,
  };
  channel.postMessage(message);

  return () => {
    channel.close();
  };
}
